// Shared pieces for the Accounts screens.
import { useState, useEffect } from "react";
import { Modal, Button, Field, TextArea, Notice } from "./kit/components";
import { T, text } from "./kit/tokens";

export const TYPE_INFO = {
    asset: ["Assets", "What the company owns or is owed"],
    liability: ["Liabilities", "What the company owes to others"],
    equity: ["Equity", "Owners' investment and profit kept in the business"],
    income: ["Income", "Money the company earns"],
    expense: ["Expenses", "Costs of running the business"],
};
export const CASH_FLOW = [
    ["", "Not part of the cash-flow statement"],
    ["cash", "Cash & bank"],
    ["operating", "Day-to-day business"],
    ["investing", "Equipment & investments"],
    ["financing", "Owners & loans"],
];
export const cashFlowLabel = (v) => (CASH_FLOW.find(([k]) => k === (v || "")) || [null, "—"])[1];
export const ROLE_LABELS = {
    shareholder: "Shareholder", director: "Director", staff: "Staff", freelancer: "Freelancer",
    customer: "Customer", vendor: "Supplier", tenant: "Tenant", other: "Other",
};

export function Chip({ children, tone }) {
    const color = tone || T.textMuted;
    return <span style={{ display: "inline-block", fontSize: 12, padding: "2px 8px", borderRadius: 999, border: `1px solid ${color}55`, color, marginRight: 6, marginBottom: 2, whiteSpace: "nowrap" }}>{children}</span>;
}

export function SectionCard({ title, subtitle, actions, children, pad = 16 }) {
    return (
        <section style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, marginBottom: 20 }}>
            {(title || actions) && (
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", padding: "14px 16px", borderBottom: `1px solid ${T.borderSoft}` }}>
                    <div style={{ minWidth: 0 }}>
                        <h3 style={text.section}>{title}</h3>
                        {subtitle && <div style={{ ...text.caption, marginTop: 4, maxWidth: 720 }}>{subtitle}</div>}
                    </div>
                    {actions && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div>}
                </div>
            )}
            <div style={{ padding: pad }}>{children}</div>
        </section>
    );
}

// Asks for a written reason (stored in the audit log) before a sensitive action.
export function ReasonModal({ open, title, intro, confirmLabel = "Confirm", tone = "primary", reasonLabel = "Reason", reasonHint, onConfirm, onClose, children, canConfirm = true }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => { if (open) { setReason(""); setErr(""); setBusy(false); } }, [open]);
    const submit = async () => {
        if (!reason.trim()) { setErr("Please write a reason. It is kept in the activity log."); return; }
        setBusy(true); setErr("");
        try { await onConfirm(reason.trim()); } catch (e) { setErr(e?.message || "That didn't work."); setBusy(false); }
    };
    return (
        <Modal open={open} title={title} onClose={busy ? undefined : onClose}
            footer={<>
                <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant={tone} onClick={submit} disabled={busy || !canConfirm}>{busy ? "Saving…" : confirmLabel}</Button>
            </>}>
            <div style={{ display: "grid", gap: 14 }}>
                {intro && <div style={{ fontSize: 14, color: T.text, lineHeight: 1.5 }}>{intro}</div>}
                {children}
                <Field label={reasonLabel} required hint={reasonHint || "Kept permanently in the activity log."}>
                    <TextArea value={reason} onChange={e => setReason(e.target.value)} />
                </Field>
                {err && <Notice tone="danger">{err}</Notice>}
            </div>
        </Modal>
    );
}

// BS/AD date input: now in the shared UI kit (Stage 2E-2); re-exported so Accounts code is unchanged.
export { DualDateInput } from "../ui/kit/DualDateInput";

export const errText = (e) => e?.message || "Something went wrong.";

export const CHART_REVIEW_REQ = "Auditor's chart-of-accounts review recorded";
export const PROVISIONAL_ACTIVATION_NOTE = "PROVISIONAL ACTIVATION – auditor's chart-of-accounts review pending";

// What still has to happen before the books can be switched on.
export function readiness(data, cal) {
    const s = data.settings || {};
    const start = s.books_start_date;
    const fy = start ? data.years.find(y => start >= y.start_date && start <= y.end_date) : null;
    const fyMonths = fy ? data.periods.filter(p => p.fiscal_year_id === fy.id) : [];
    const fyVerified = !!fy && fyMonths.length === 12 && fyMonths.every(p => cal.month(p.bs_year, p.bs_month)?.verified);
    const verifiedMonths = data.calendar.filter(c => c.verified).length;
    const chartReview = (data.signoffs || []).find(x => x.kind === "chart_review" && !x.revoked_at) || null;
    // 021: an active owner approval may stand in for the auditor's chart review at switch-on only; the review stays pending.
    const provisionalApproval = (data.provisionalApprovals || []).find(x => !x.revoked_at) || null;
    const waived = (x) => x.requirement === CHART_REVIEW_REQ && !!provisionalApproval && !chartReview;
    return {
        pan: !!s.pan, start, fy, fyVerified, verifiedMonths, totalMonths: data.calendar.length,
        canEnable: (data.activation ? data.activation.every(x => x.ok) : (!!s.pan && !!start && !!fy && fyVerified))
            && (!data.readiness || data.readiness.every(x => x.ok || waived(x))),
        activation: data.activation ? [...data.activation, ...(data.readiness || [])] : null,
        chartReview, provisionalApproval,
        // Accounts is on without the auditor's chart review (021): shown everywhere in Accounts until the review is recorded.
        provisionalActivation: !!(data.has021 && s.enabled && !chartReview),
        otherRequirementsMet: (data.activation || []).every(x => x.ok) && (data.readiness || []).every(x => x.ok || x.requirement === CHART_REVIEW_REQ),
    };
}
